// src/auth/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { msalInstance, apiScopes } from "./msal";

type AuthCtx = {
  account: AccountInfo | null;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  ready: boolean;
};

const Ctx = createContext<AuthCtx | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Handle redirect and pick up any existing session
  useEffect(() => {
    msalInstance
      .handleRedirectPromise()
      .then((result: AuthenticationResult | null) => {
        const acc = result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        setAccount(acc);
      })
      .catch((e) => console.error("MSAL redirect handling failed", e))
      .finally(() => setReady(true));
  }, []);

  // Acquire token (silent first, redirect fallback)
  useEffect(() => {
    if (!account) return;
    const request = { scopes: apiScopes.length ? apiScopes : ["openid", "profile"], account } as const;

    msalInstance
      .acquireTokenSilent(request)
      .then((res) => setAccessToken(res.accessToken))
      .catch(() => msalInstance.acquireTokenRedirect(request));
  }, [account]);

  const login = () => {
    msalInstance.loginRedirect({
      scopes: apiScopes.length ? apiScopes : ["openid", "profile", "email"],
    });
  };

  const logout = () => msalInstance.logoutRedirect();

  return (
    <Ctx.Provider value={{ account, accessToken, login, logout, ready }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuthContext = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuthContext must be used within <AuthProvider>");
  return ctx;
};